import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Compact table/row action: icon only, label via tooltip + aria-label. */
export function ActionIconButton({
  label,
  onClick,
  className,
  children,
  disabled,
  stopPropagation,
  size = 'md',
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
  stopPropagation?: boolean;
  /** `sm` for denser lists (keywords, chips). */
  size?: 'sm' | 'md';
}) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        if (!disabled) onClick();
      }}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900',
        size === 'sm' ? 'h-7 w-7' : 'h-8 w-8',
        disabled && 'opacity-30',
        className,
      )}
    >
      {children}
    </button>
  );

  return (
    <Tooltip>
      {/* Disabled buttons don't fire pointer events — wrap so the tooltip still works. */}
      <TooltipTrigger asChild>
        {disabled ? <span className="inline-flex">{button}</span> : button}
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
